import { useEffect, useMemo, useRef, useState } from 'react'
import { useTaskReminder } from '@/modules/tasks/hooks/useTasks'
import {
  REMINDER_CYCLE_GAP_MS,
  REMINDER_FIRST_DELAY_MS,
  REMINDER_LOOPS_PER_BURST,
  REMINDER_STATUS_HOLD_MS,
  readReminderSession,
  taskReminderSlides,
  writeReminderSession,
  type TaskReminderCycleOptions,
  type TaskReminderKind,
  type TaskReminderSlide,
} from '@/modules/tasks/utils/task-reminder'

type Phase = 'waiting' | 'showing' | 'resting' | 'done'

/**
 * Shared reminder burst for desktop chip + mobile pill.
 *
 * Session rules (locked):
 * - Clock starts once when the user opens the app (browser tab session).
 * - Survives refresh via sessionStorage — does not restart the delay / re-burst.
 * - Does not restart on module changes, focus, or visibility.
 * - `oncePerSession`: after the first burst, stay quiet for the rest of the session.
 */
export function useTaskReminderCycle(enabled: boolean, options?: TaskReminderCycleOptions) {
  const firstDelayMs = options?.firstDelayMs ?? REMINDER_FIRST_DELAY_MS
  const loopsPerBurst = options?.loopsPerBurst ?? REMINDER_LOOPS_PER_BURST
  const cycleGapMs = options?.cycleGapMs ?? REMINDER_CYCLE_GAP_MS
  const statusHoldMs = options?.statusHoldMs ?? REMINDER_STATUS_HOLD_MS
  const oncePerSession = options?.oncePerSession ?? false

  const query = useTaskReminder(enabled)
  const slides = useMemo(() => taskReminderSlides(query.data), [query.data])
  const dataReady = enabled && !query.isPending && slides.length > 0

  const [phase, setPhase] = useState<Phase>('waiting')
  const [step, setStep] = useState(0)
  const [burstVisible, setBurstVisible] = useState(false)
  const [boot, setBoot] = useState(0)
  const [dismissedKinds, setDismissedKinds] = useState(() => new Set<TaskReminderKind>())
  /** Ensures we only seed the session clock / phase once per mount lifecycle. */
  const sessionSeededRef = useRef(false)
  const phaseRef = useRef(phase)

  useEffect(() => {
    phaseRef.current = phase
  }, [phase])

  const activeSlides = useMemo(
    () => slides.filter((slide) => !dismissedKinds.has(slide.kind)),
    [slides, dismissedKinds],
  )

  const totalSteps = activeSlides.length * loopsPerBurst
  const current: TaskReminderSlide | null =
    activeSlides.length > 0 ? (activeSlides[step % activeSlides.length] ?? null) : null

  const markBurstCompleted = () => {
    const existing = readReminderSession()
    writeReminderSession({
      startedAt: existing?.startedAt ?? Date.now(),
      burstCompleted: true,
    })
  }

  // Seed from sessionStorage once data is ready — never on focus / slideKey / module toggle.
  /* eslint-disable react-hooks/set-state-in-effect -- session-gated phase machine */
  useEffect(() => {
    if (!enabled) {
      setBurstVisible(false)
      return
    }
    if (!dataReady || sessionSeededRef.current) return
    sessionSeededRef.current = true

    const existing = readReminderSession()
    if (existing?.burstCompleted && oncePerSession) {
      setPhase('done')
      setBurstVisible(false)
      setStep(0)
      return
    }

    const startedAt = existing?.startedAt ?? Date.now()
    if (!existing) {
      writeReminderSession({ startedAt, burstCompleted: false })
    }

    setDismissedKinds(new Set())
    setStep(0)
    setBurstVisible(false)
    setPhase('waiting')
    setBoot((n) => n + 1)
  }, [enabled, dataReady, oncePerSession])

  useEffect(() => {
    if (!enabled || !dataReady) {
      setBurstVisible(false)
      return
    }
    if (phase === 'done') return

    const timers: number[] = []
    const clear = () => {
      for (const id of timers) window.clearTimeout(id)
    }

    if (phase === 'waiting') {
      const session = readReminderSession()
      const startedAt = session?.startedAt ?? Date.now()
      if (!session) {
        writeReminderSession({ startedAt, burstCompleted: false })
      }
      const remaining = Math.max(0, startedAt + firstDelayMs - Date.now())
      timers.push(
        window.setTimeout(() => {
          if (readReminderSession()?.burstCompleted && oncePerSession) {
            setPhase('done')
            setBurstVisible(false)
            return
          }
          setDismissedKinds(new Set())
          setStep(0)
          setBurstVisible(true)
          setPhase('showing')
        }, remaining),
      )
    } else if (phase === 'showing') {
      if (activeSlides.length === 0) {
        markBurstCompleted()
        setBurstVisible(false)
        setPhase(oncePerSession ? 'done' : 'resting')
        return clear
      }
      timers.push(
        window.setTimeout(() => {
          const next = step + 1
          if (next >= totalSteps) {
            markBurstCompleted()
            setBurstVisible(false)
            setPhase(oncePerSession ? 'done' : 'resting')
            return
          }
          setStep(next)
        }, statusHoldMs),
      )
    } else if (phase === 'resting' && !oncePerSession) {
      timers.push(
        window.setTimeout(() => {
          setDismissedKinds(new Set())
          setStep(0)
          setBurstVisible(true)
          setPhase('showing')
        }, cycleGapMs),
      )
    }

    return clear
  }, [
    phase,
    step,
    enabled,
    dataReady,
    totalSteps,
    boot,
    activeSlides.length,
    firstDelayMs,
    statusHoldMs,
    cycleGapMs,
    oncePerSession,
  ])
  /* eslint-enable react-hooks/set-state-in-effect */

  const dismiss = () => {
    if (!current) return
    const kind = current.kind
    const remaining = activeSlides.filter((slide) => slide.kind !== kind)
    setDismissedKinds((prev) => {
      const next = new Set(prev)
      next.add(kind)
      return next
    })
    if (remaining.length === 0) {
      markBurstCompleted()
      setBurstVisible(false)
      setPhase(oncePerSession ? 'done' : 'resting')
      return
    }
    setStep(0)
    setBurstVisible(true)
    setPhase('showing')
    setBoot((n) => n + 1)
  }

  return {
    current: activeSlides.length > 0 ? (current ?? activeSlides[0] ?? null) : null,
    /** Desktop chip: show whenever counts exist (quiet between bursts). */
    visible: enabled && activeSlides.length > 0,
    /** Attention / mobile banner — only during the timed burst. */
    attention: burstVisible && phase === 'showing',
    isPending: query.isPending,
    dismiss,
  }
}
